import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Search, X } from 'lucide-react';
import { displayMessage } from '../Utils/helper';
import { stageLayoutStyles, stageFormStyles } from '../components/createworkflow/helper';
import PageHeader from '../components/common/PageHeader';

const STORAGE_KEY = 'scm_vendors';

const emptyForm = { vendor_name: '', poc: '', email: '', pin: '', city: '' };
const FIELDS = [
  { name: 'vendor_name', label: 'Vendor Name', type: 'text', placeholder: 'Enter vendor name' },
  { name: 'poc', label: 'POC', type: 'text', placeholder: 'Point of contact' },
  { name: 'email', label: 'Email Id', type: 'email', placeholder: 'Enter email address' },
  { name: 'pin', label: 'PIN', type: 'text', placeholder: 'PIN code' },
  { name: 'city', label: 'City', type: 'text', placeholder: 'City' },
];

/* ========== DUMMY DATA (active now) ==========*/
const DUMMY_SKU_COMBINATIONS = [
  'CPG\\Personal care\\Haircare\\H&S\\Hair Product\\Hair Shampoo',
  'CPG\\Personal care\\Haircare\\H&S\\Hair Product\\Conditioner',
  'CPG\\Personal care\\Haircare\\H&S\\Hair Product\\Hair Mask',
  'CPG\\Personal care\\Haircare\\H&S\\Hair Product\\Hair Colour',
  'CPG\\Personal care\\skincare\\Facewash\\VLCC\\VLCC Face Wash',
  'CPG\\Personal care\\Soaps\\Santoor\\Standard Type\\Santoor Soap',
  'CPG\\Personal care\\Femcare\\Stayfree\\Standard Type\\Stayfree Regular',
  'CPG\\Personal care\\Femcare\\Whisper\\Standard Type\\Whisper Ultra',
  'Automobile\\Automatic\\Crossover\\Honda HRV\\Model\\Honda HRV VX',
  'Automobile\\Automatic\\Crossover\\Honda HRV\\Model\\FortunerGR-S',
  'Pharma\\OTC Drugs\\Cold & Cough\\Vicks Action 500\\Standard Type\\Vicks Action 500',
  'Pharma\\OTC Drugs\\Cold & Cough\\Vicks Action 500\\Standard Type\\Vicks Advanced',
  'Technology\\Laptops\\Business Laptop\\Dell Lattitude\\Windows\\Dell Latitude 5440',
  'Technology\\Laptops\\Business Laptop\\Dell Lattitude\\Windows\\Dell Latitude i5',
  'Tyres\\Eco Tyres\\Mud Terrain Tyres\\MRF ZLR\\Standard Type\\MRF ZLR',
  'Tyres\\Eco Tyres\\Mud Terrain Tyres\\MRF ZLR\\Standard Type\\MRF Revz',
];

const LEVEL_COLORS = ['#032B4E', '#0a4a78', '#0d9488', '#059669', '#d97706', '#E06639', '#7c3aed', '#be185d'];

const parseSkuPath = (sku) =>
  String(sku || '')
    .split(/[\\/]/)
    .map((part) => part.trim())
    .filter(Boolean);

const skuLeafName = (sku) => {
  const parts = parseSkuPath(sku);
  return parts[parts.length - 1] || String(sku || '');
};

/** Normalize backend SKU rows (string | object) into combination paths. Depth is flexible. */
const normalizeSkuCatalog = (rawList = []) => {
  const seen = new Set();
  const catalog = [];

  rawList.forEach((item, index) => {
    let combination = '';
    let skuId = null;
    let skuName = null;

    if (typeof item === 'string') {
      combination = item;
    } else if (item && typeof item === 'object') {
      combination =
        item.combination ||
        item.breadcrumb ||
        item.path ||
        item.sku ||
        '';
      if (!combination) {
        const levelParts = [];
        for (let i = 1; i <= 12; i += 1) {
          const val = item[`level${i}`] || item[`level${i}_name`];
          if (val) levelParts.push(String(val).trim());
        }
        const leaf = item.sku_name || item.name || item.label;
        if (leaf) levelParts.push(String(leaf).trim());
        combination = levelParts.filter(Boolean).join('\\');
      }
      skuId = item.sku_id ?? item.id ?? null;
      skuName = item.sku_name || item.name || null;
    }

    combination = String(combination || '')
      .replace(/\//g, '\\')
      .split('\\')
      .map((p) => p.trim())
      .filter(Boolean)
      .join('\\');

    if (!combination || seen.has(combination)) return;
    seen.add(combination);
    catalog.push({
      combination,
      sku_id: skuId,
      sku_name: skuName || skuLeafName(combination),
      depth: parseSkuPath(combination).length,
      category: parseSkuPath(combination)[0] || 'Uncategorized',
      _index: index,
    });
  });

  return catalog;
};

const getCatalogStats = (catalog = [], selectedKeys = []) => {
  const categories = new Set();
  let maxDepth = 0;
  let minDepth = Infinity;
  catalog.forEach((row) => {
    categories.add(row.category);
    maxDepth = Math.max(maxDepth, row.depth || 0);
    minDepth = Math.min(minDepth, row.depth || 0);
  });
  if (!catalog.length) minDepth = 0;

  const selectedCats = new Set();
  selectedKeys.forEach((key) => {
    const parts = parseSkuPath(key);
    if (parts[0]) selectedCats.add(parts[0]);
  });

  return {
    totalSkus: catalog.length,
    categories: categories.size,
    maxDepth,
    minDepth,
    selectedSkus: selectedKeys.length,
    selectedCategories: selectedCats.size,
  };
};

const buildSkuTree = (catalog = []) => {
  const root = new Map();
  catalog.forEach((row) => {
    const combination = typeof row === 'string' ? row : row.combination;
    const parts = parseSkuPath(combination);
    if (!parts.length) return;
    let map = root;
    parts.forEach((part, index) => {
      const isLeaf = index === parts.length - 1;
      if (!map.has(part)) {
        map.set(part, {
          name: part,
          sku: isLeaf ? combination : null,
          isLeaf,
          level: index + 1,
          children: new Map(),
        });
      } else if (isLeaf) {
        map.get(part).sku = combination;
        map.get(part).isLeaf = true;
        map.get(part).level = index + 1;
      }
      map = map.get(part).children;
    });
  });

  const toArray = (map) =>
    Array.from(map.values())
      .map((node) => {
        const children = toArray(node.children);
        const leaves =
          node.isLeaf && node.sku
            ? [node.sku]
            : children.flatMap((child) => child.leaves);
        return { ...node, children, leaves, leafCount: leaves.length };
      })
      .sort((a, b) => {
        if (a.isLeaf !== b.isLeaf) return a.isLeaf ? 1 : -1;
        return a.name.localeCompare(b.name);
      });

  return toArray(root);
};

const groupSkusByCategory = (skuItems = []) => {
  const groups = new Map();
  skuItems.forEach((item) => {
    const sku = typeof item === 'string' ? item : item?.sku;
    if (!sku) return;
    const parts = parseSkuPath(sku);
    const category = parts[0] || 'Uncategorized';
    if (!groups.has(category)) groups.set(category, []);
    groups.get(category).push(typeof item === 'string' ? { sku } : item);
  });
  return Array.from(groups.entries()).map(([category, items]) => ({ category, items }));
};

const normalizeSkuAgainstCatalog = (sku, catalog = []) => {
  if (!sku) return sku;
  const keys = catalog.map((row) => (typeof row === 'string' ? row : row.combination));
  if (keys.includes(sku)) return sku;
  const leaf = skuLeafName(sku).toLowerCase();
  return keys.find((item) => skuLeafName(item).toLowerCase() === leaf) || sku;
};

const nodeMatches = (node, query) => {
  if (!query) return true;
  if (node.name.toLowerCase().includes(query)) return true;
  if (node.sku && node.sku.toLowerCase().includes(query)) return true;
  return node.children.some((child) => nodeMatches(child, query));
};

const visibleLeaves = (node, query) => {
  if (!query) return node.leaves;
  if (node.isLeaf) return nodeMatches(node, query) ? [node.sku] : [];
  return node.children.flatMap((child) => visibleLeaves(child, query));
};

function TriCheckbox({ checked, indeterminate, onChange }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      onClick={(e) => e.stopPropagation()}
      style={skuUi.checkbox}
    />
  );
}

function LevelBadge({ level, isLeaf }) {
  const color = LEVEL_COLORS[(level - 1) % LEVEL_COLORS.length];
  return (
    <span
      style={{
        ...skuUi.levelBadge,
        color,
        background: `${color}14`,
        border: `1px solid ${color}33`,
      }}
    >
      {isLeaf ? 'SKU' : `L${level}`}
    </span>
  );
}

function AccordionNode({ node, path, openPath, setOpenPath, selectedSet, toggleSkus, query, depth }) {
  if (query && !nodeMatches(node, query)) return null;

  const leaves = visibleLeaves(node, query);
  const selectedCount = leaves.filter((sku) => selectedSet.has(sku)).length;
  const allSelected = leaves.length > 0 && selectedCount === leaves.length;
  const someSelected = selectedCount > 0 && !allSelected;
  const hasChildren = !node.isLeaf && node.children.length > 0;
  const isOpen = query
    ? true
    : openPath.length >= path.length && path.every((part, i) => openPath[i] === part);

  return (
    <div>
      <div
        style={{
          ...skuUi.row,
          paddingLeft: 8 + depth * 14,
          background: allSelected ? '#eff7fc' : 'transparent',
        }}
        onClick={() => {
          if (!hasChildren) return;
          if (isOpen && openPath.length === path.length) setOpenPath(path.slice(0, -1));
          else setOpenPath(path);
        }}
        title={node.isLeaf ? node.sku : `${node.name} · ${node.leafCount} SKU${node.leafCount === 1 ? '' : 's'}`}
      >
        {hasChildren ? (
          <span style={skuUi.chevron}>{isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span>
        ) : (
          <span style={skuUi.chevronSpacer} />
        )}
        <TriCheckbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={() => toggleSkus(leaves, !allSelected)}
        />
        <LevelBadge level={node.level} isLeaf={node.isLeaf} />
        <span style={{ ...skuUi.rowLabel, fontWeight: node.isLeaf ? 500 : 600 }}>{node.name}</span>
        {!node.isLeaf && (
          <span style={skuUi.branchMeta}>
            {selectedCount > 0 ? `${selectedCount}/` : ''}
            {node.leafCount}
          </span>
        )}
      </div>
      {hasChildren && isOpen &&
        node.children.map((child) => (
          <AccordionNode
            key={child.sku || `${path.join('\\')}\\${child.name}`}
            node={child}
            path={[...path, child.name]}
            openPath={openPath}
            setOpenPath={setOpenPath}
            selectedSet={selectedSet}
            toggleSkus={toggleSkus}
            query={query}
            depth={depth + 1}
          />
        ))}
    </div>
  );
}

function SkuAccordionPicker({ catalog = [], selectedSkus = [], onChange }) {
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [openPath, setOpenPath] = useState([]);
  const query = search.trim().toLowerCase();
  const tree = useMemo(() => buildSkuTree(catalog), [catalog]);
  const selectedSet = useMemo(() => new Set(selectedSkus), [selectedSkus]);
  const stats = useMemo(() => getCatalogStats(catalog, selectedSkus), [catalog, selectedSkus]);

  const closePicker = () => {
    setOpen(false);
    setSearch('');
    setOpenPath([]);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) closePicker();
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const toggleOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (open) closePicker();
    else setOpen(true);
  };

  const toggleSkus = (skus, shouldSelect) => {
    const next = new Set(selectedSet);
    skus.forEach((sku) => {
      if (shouldSelect) next.add(sku);
      else next.delete(sku);
    });
    onChange(Array.from(next));
  };

  const summary =
    selectedSkus.length === 0
      ? 'Select SKUs from product hierarchy...'
      : selectedSkus
          .slice(0, 2)
          .map(skuLeafName)
          .join(', ') + (selectedSkus.length > 2 ? ` +${selectedSkus.length - 2}` : '');

  const depthLabel =
    stats.minDepth === stats.maxDepth
      ? `${stats.maxDepth} levels`
      : `${stats.minDepth}–${stats.maxDepth} levels`;

  return (
    <div ref={wrapRef} style={skuUi.wrap}>
      <div style={skuUi.statsRow} data-sku-stats>
        <div style={skuUi.statChip}>
          <span style={skuUi.statValue}>{stats.categories}</span>
          <span style={skuUi.statLabel}>Categories</span>
        </div>
        <div style={skuUi.statChip}>
          <span style={skuUi.statValue}>{stats.maxDepth || 0}</span>
          <span style={skuUi.statLabel}>{depthLabel}</span>
        </div>
        <div style={skuUi.statChip}>
          <span style={skuUi.statValue}>{stats.totalSkus}</span>
          <span style={skuUi.statLabel}>Total SKUs</span>
        </div>
        <div style={{ ...skuUi.statChip, ...skuUi.statChipActive }}>
          <span style={skuUi.statValue}>{stats.selectedSkus}</span>
          <span style={skuUi.statLabel}>
            Selected{stats.selectedCategories ? ` · ${stats.selectedCategories} cat.` : ''}
          </span>
        </div>
      </div>

      <button
        type="button"
        style={{
          ...skuUi.trigger,
          borderRadius: open ? '8px 8px 0 0' : 8,
        }}
        onClick={toggleOpen}
        aria-expanded={open}
      >
        <span style={{ ...skuUi.triggerText, color: selectedSkus.length ? '#0c3c54' : '#6a7d8c' }}>{summary}</span>
        <ChevronDown size={16} color="#6a7d8c" style={{ transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
      </button>

      {open ? (
        <div style={skuUi.panel}>
          <div style={skuUi.searchWrap}>
            <Search size={14} color="#6a7d8c" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search any level or SKU..."
              style={skuUi.searchInput}
            />
            {search ? (
              <button type="button" style={skuUi.iconBtn} onClick={() => setSearch('')}>
                <X size={14} />
              </button>
            ) : null}
          </div>

          <div style={skuUi.legend}>
            <span>L1 = Category</span>
            <span>·</span>
            <span>Middle = Sub-levels</span>
            <span>·</span>
            <span>SKU = Leaf item</span>
          </div>

          <div style={skuUi.tree}>
            {tree.length === 0 ? (
              <p style={skuUi.empty}>No SKUs available from catalog</p>
            ) : query && !tree.some((node) => nodeMatches(node, query)) ? (
              <p style={skuUi.empty}>No SKUs match “{search.trim()}”</p>
            ) : (
              tree.map((node) => (
                <AccordionNode
                  key={node.name}
                  node={node}
                  path={[node.name]}
                  openPath={openPath}
                  setOpenPath={setOpenPath}
                  selectedSet={selectedSet}
                  toggleSkus={toggleSkus}
                  query={query}
                  depth={0}
                />
              ))
            )}
          </div>

          {selectedSkus.length > 0 && (
            <div style={skuUi.selectedBar}>
              {groupSkusByCategory(selectedSkus.map((sku) => ({ sku }))).map(({ category, items }) => (
                <div key={category} style={skuUi.selectedGroup}>
                  <div style={skuUi.selectedGroupHead}>
                    <span>{category}</span>
                    <span style={skuUi.branchMeta}>{items.length}</span>
                  </div>
                  <div style={skuUi.chipWrap}>
                    {items.map((item) => (
                      <span key={item.sku} style={skuUi.chip} title={item.sku}>
                        {skuLeafName(item.sku)}
                        <button type="button" style={skuUi.chipRemove} onClick={() => toggleSkus([item.sku], false)}>
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

const readLocal = (catalog = []) => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (!Array.isArray(stored)) return [];
    return stored.map((vendor) => ({
      ...vendor,
      skus: (vendor.skus || []).map((item) => ({
        ...item,
        sku: normalizeSkuAgainstCatalog(item?.sku, catalog),
      })),
    }));
  } catch {
    return [];
  }
};

export default function VendorConfigPage() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [skuCatalog, setSkuCatalog] = useState(() => normalizeSkuCatalog(DUMMY_SKU_COMBINATIONS));

  useEffect(() => {
    // --- CURRENT: load dummy SKU catalog + local vendors ---
    const catalog = normalizeSkuCatalog(DUMMY_SKU_COMBINATIONS);
    setSkuCatalog(catalog);
    setList(readLocal(catalog));

    // --- BACKEND: uncomment when APIs are ready, then remove the CURRENT block above ---
    // const loadFromApi = async () => {
    //   try {
    //     // GET product hierarchy / SKU list (combination paths, any depth)
    //     // const apiRows = await fetchProductSkus();
    //     // const catalog = normalizeSkuCatalog(apiRows);
    //     // setSkuCatalog(catalog);
    //
    //     // GET /scm/vendors
    //     // const vendors = await fetchVendors();
    //     // setList(vendors);
    //   } catch (err) {
    //     console.error('VendorConfig API load failed', err);
    //   }
    // };
    // loadFromApi();
  }, []);

  const editing = list.find((el) => el.vendor_id === editingId);

  const resetForm = () => {
    setForm(emptyForm);
    setSelectedSkus([]);
    setEditingId(null);
    setShowDelete(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSkuSelect = (skus) => {
    setSelectedSkus((skus || []).map((sku) => ({ geo_location: 'India', sku })));
  };

  const handleEdit = (vendor) => {
    setEditingId(vendor.vendor_id);
    setShowDelete(false);
    setForm({
      vendor_name: vendor.vendor_name || '',
      poc: vendor.poc || '',
      email: vendor.email || '',
      pin: vendor.pin || '',
      city: vendor.city || '',
    });
    setSelectedSkus(Array.isArray(vendor.skus) ? vendor.skus : []);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = () => {
    if (editingId == null) return;
    const next = list.filter((el) => el.vendor_id !== editingId);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setList(next);
    if (expandedId === editingId) setExpandedId(null);
    resetForm();
    // BACKEND: uncomment when ready → await deleteVendor(editingId)
    displayMessage('success', 'Success', 'Vendor deleted successfully.');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const vendor_name = form.vendor_name.trim();
    const poc = form.poc.trim();
    const email = form.email.trim();
    const pin = form.pin.trim();
    const city = form.city.trim();
    const isEditing = editingId != null;

    if (!vendor_name || !poc || !email || !pin || !city) {
      displayMessage('danger', 'Invalid Input', 'Vendor name, POC, email, PIN and city are all required.');
      return;
    }
    if (selectedSkus.length === 0) {
      displayMessage('danger', 'Invalid Input', 'Select at least one SKU this vendor is responsible for.');
      return;
    }
    if (list.some((el) => el.email?.toLowerCase() === email.toLowerCase() && el.vendor_id !== editingId)) {
      displayMessage('danger', 'Duplicate Entry', `${email} is already in the vendor list.`);
      return;
    }

    setLoading(true);
    try {
      const payload = { vendor_name, poc, email, pin, city, skus: selectedSkus };
      const next = isEditing
        ? list.map((el) => (el.vendor_id === editingId ? { ...el, ...payload } : el))
        : [...list, { vendor_id: Date.now(), ...payload }];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      setList(next);
      // BACKEND: uncomment when ready →
      // isEditing ? await updateVendor(editingId, payload) : await createVendor(payload)
      resetForm();
      displayMessage('success', 'Success', isEditing ? 'Vendor updated successfully.' : 'Vendor added successfully.');
    } catch (err) {
      console.error(err);
      displayMessage('danger', 'Error', isEditing ? 'Error updating vendor.' : 'Error adding vendor.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="vendor-config-page" style={pageStyles.wrapper}>
      <PageHeader
        title="Vendor Config"
        subtitle="Add vendors with contact details and the SKUs they are responsible for."
      />

      <div style={pageStyles.card}>
        <h2 style={pageStyles.cardTitle}>
          {editingId != null ? 'Edit Vendor' : 'Add Vendor'}
        </h2>
        <form onSubmit={handleSave}>
          <div style={pageStyles.formGrid}>
            {FIELDS.map((f) => (
              <div key={f.name} style={pageStyles.formField}>
                <label style={stageFormStyles.label}>{f.label}</label>
                <input
                  type={f.type}
                  name={f.name}
                  placeholder={f.placeholder}
                  value={form[f.name]}
                  onChange={handleChange}
                  required
                  style={stageFormStyles.input}
                />
              </div>
            ))}
          </div>

          <div style={pageStyles.skuBlock}>
            <div style={pageStyles.skuTop}>
              <label style={stageFormStyles.label}>SKUs this vendor is responsible for</label>
              {selectedSkus.length > 0 && (
                <span style={pageStyles.count}>{selectedSkus.length} selected</span>
              )}
            </div>
            <SkuAccordionPicker
              key={editingId || 'new'}
              catalog={skuCatalog}
              selectedSkus={selectedSkus.map((s) => s.sku)}
              onChange={handleSkuSelect}
            />
          </div>

          <div style={pageStyles.actions}>
            {editingId != null && (
              <>
                <button
                  type="button"
                  style={pageStyles.dangerOutlineBtn}
                  onClick={() => setShowDelete(true)}
                  disabled={loading}
                >
                  Delete
                </button>
                <button
                  type="button"
                  style={pageStyles.outlineBtn}
                  onClick={resetForm}
                  disabled={loading}
                >
                  Cancel
                </button>
              </>
            )}
            <button
              type="submit"
              disabled={loading}
              style={{
                ...pageStyles.primaryBtn,
                ...(loading ? pageStyles.primaryBtnDisabled : {}),
              }}
            >
              {loading ? 'Saving...' : editingId != null ? 'Update' : 'Save'}
            </button>
          </div>
        </form>
      </div>

      <div style={pageStyles.card}>
        <h2 style={pageStyles.cardTitle}>Vendor Directory</h2>
        <div style={pageStyles.tableWrap}>
          <table style={pageStyles.table}>
            <thead>
              <tr>
                <th style={pageStyles.th}>Vendor</th>
                <th style={pageStyles.th}>POC</th>
                <th style={pageStyles.th}>Email</th>
                <th style={pageStyles.th}>PIN</th>
                <th style={pageStyles.th}>City</th>
                <th style={pageStyles.th}>Assigned SKUs</th>
                <th style={pageStyles.thLast}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.length > 0 ? (
                list.map((vendor) => (
                  <React.Fragment key={vendor.vendor_id}>
                    <tr style={editingId === vendor.vendor_id ? pageStyles.rowActive : undefined}>
                      <td style={pageStyles.td}>
                        <div style={pageStyles.nameCell}>
                          <div style={pageStyles.avatar}>
                            {vendor.vendor_name?.charAt(0)?.toUpperCase()}
                          </div>
                          <span style={pageStyles.nameText}>{vendor.vendor_name}</span>
                        </div>
                      </td>
                      <td style={pageStyles.tdMuted}>{vendor.poc}</td>
                      <td style={pageStyles.tdMuted}>{vendor.email}</td>
                      <td style={pageStyles.tdMuted}>{vendor.pin}</td>
                      <td style={pageStyles.tdMuted}>{vendor.city}</td>
                      <td style={pageStyles.tdCenter}>
                        <button
                          type="button"
                          style={pageStyles.navyBtn}
                          onClick={() =>
                            setExpandedId((prev) =>
                              prev === vendor.vendor_id ? null : vendor.vendor_id
                            )
                          }
                        >
                          {expandedId === vendor.vendor_id ? 'Hide SKUs' : 'View SKUs'}
                          {vendor.skus?.length ? ` (${vendor.skus.length})` : ''}
                        </button>
                      </td>
                      <td style={pageStyles.tdCenterLast}>
                        <button
                          type="button"
                          style={pageStyles.secondaryBtn}
                          onClick={() => handleEdit(vendor)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                    {expandedId === vendor.vendor_id && (
                      <tr>
                        <td colSpan="7" style={pageStyles.expandCell}>
                          <div style={pageStyles.foldPanel}>
                            <h4 style={pageStyles.foldTitle}>
                              SKU Assignments for {vendor.vendor_name}
                            </h4>
                            {vendor.skus?.length > 0 ? (
                              <div style={pageStyles.assignedWrap}>
                                <div style={pageStyles.assignedStats}>
                                  <span>{vendor.skus.length} SKUs</span>
                                  <span>·</span>
                                  <span>{groupSkusByCategory(vendor.skus).length} categories</span>
                                  <span>·</span>
                                  <span>
                                    up to{' '}
                                    {Math.max(
                                      ...vendor.skus.map((item) => parseSkuPath(item.sku).length),
                                      0
                                    )}{' '}
                                    levels
                                  </span>
                                </div>
                                {groupSkusByCategory(vendor.skus).map(({ category, items }) => (
                                  <div key={category} style={pageStyles.assignedGroup}>
                                    <div style={pageStyles.assignedHead}>
                                      <span>{category}</span>
                                      <span style={pageStyles.assignedCount}>{items.length}</span>
                                    </div>
                                    <div style={pageStyles.chipWrap}>
                                      {items.map((item, idx) => (
                                        <span
                                          key={`${item.sku}-${idx}`}
                                          style={pageStyles.tag}
                                          title={item.sku}
                                        >
                                          {skuLeafName(item.sku)}
                                          <em style={pageStyles.tagDepth}>
                                            L{parseSkuPath(item.sku).length}
                                          </em>
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p style={{ ...pageStyles.emptyText, padding: '12px 0' }}>
                                No SKU assignments found
                              </p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={pageStyles.emptyText}>
                    No vendors found. Add your first vendor above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showDelete && editing && (
        <div style={pageStyles.modalOverlay} onClick={() => setShowDelete(false)}>
          <div style={pageStyles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={pageStyles.modalHead}>Delete Vendor</div>
            <div style={pageStyles.modalBody}>
              <p style={pageStyles.modalText}>
                Are you sure you want to delete <strong>{editing.vendor_name}</strong>?
              </p>
              <p style={pageStyles.modalHint}>
                This action cannot be undone. Assigned SKUs for this vendor will also be removed.
              </p>
              <div style={pageStyles.actions}>
                <button
                  type="button"
                  style={pageStyles.outlineBtn}
                  onClick={() => setShowDelete(false)}
                >
                  Cancel
                </button>
                <button type="button" style={pageStyles.dangerBtn} onClick={handleDelete}>
                  Delete Vendor
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{pageCss}</style>
    </div>
  );
}

/* ==========================================================================
 * STYLES (kept at end of file)
 * pageStyles  → page layout / table / modal
 * skuUi       → SKU accordion picker
 * pageCss     → injected <style> tag overrides
 * ========================================================================== */

const pageStyles = {
  wrapper: {
    width: '100%',
    boxSizing: 'border-box',
    background: '#f4f3ef',
  },
  card: {
    ...stageLayoutStyles.card,
    marginBottom: 14,
  },
  cardTitle: {
    ...stageFormStyles.sectionTitle,
    marginBottom: 12,
    paddingBottom: 10,
    borderBottom: '1px solid #e6eef4',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 14,
    marginBottom: 16,
  },
  formField: {
    minWidth: 0,
  },
  skuBlock: {
    marginBottom: 16,
  },
  skuTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
    gap: 8,
  },
  count: {
    background: '#032B4E',
    color: '#fff',
    borderRadius: 999,
    padding: '3px 10px',
    font: '600 12px Poppins, sans-serif',
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  primaryBtn: {
    padding: '8px 18px',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'Poppins, sans-serif',
    color: '#fff',
    background: '#E06639',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    boxShadow: '0 3px 10px rgba(224, 102, 57, 0.25)',
    whiteSpace: 'nowrap',
  },
  primaryBtnDisabled: {
    background: '#9ca3af',
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
  outlineBtn: {
    padding: '7px 16px',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'Poppins, sans-serif',
    color: '#032B4E',
    background: '#fff',
    border: '1.5px solid #032B4E',
    borderRadius: 8,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  dangerOutlineBtn: {
    padding: '7px 16px',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'Poppins, sans-serif',
    color: '#b91c1c',
    background: '#fff',
    border: '1.5px solid #dc2626',
    borderRadius: 8,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  dangerBtn: {
    padding: '8px 18px',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'Poppins, sans-serif',
    color: '#fff',
    background: '#b91c1c',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  secondaryBtn: {
    display: 'inline-block',
    padding: '7px 14px',
    fontSize: 12,
    fontWeight: 700,
    fontFamily: 'Poppins, sans-serif',
    color: '#fff',
    background: '#c9552a',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(201, 85, 42, 0.28)',
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
  },
  navyBtn: {
    display: 'inline-block',
    padding: '7px 14px',
    fontSize: 12,
    fontWeight: 700,
    fontFamily: 'Poppins, sans-serif',
    color: '#fff',
    background: '#032B4E',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
  },
  tableWrap: {
    width: '100%',
    overflowX: 'auto',
    borderRadius: 12,
    border: '1px solid #e6eef4',
    boxShadow: '0 3px 12px rgba(12, 60, 84, 0.06)',
    background: '#fff',
  },
  table: {
    width: '100%',
    minWidth: 900,
    borderCollapse: 'collapse',
    fontFamily: 'Poppins, sans-serif',
  },
  th: {
    background: '#032B4E',
    color: '#fff',
    padding: '12px 14px',
    fontSize: 12,
    fontWeight: 700,
    textAlign: 'center',
    borderRight: '1px solid rgba(255,255,255,0.1)',
    whiteSpace: 'nowrap',
  },
  thLast: {
    background: '#032B4E',
    color: '#fff',
    padding: '12px 14px',
    fontSize: 12,
    fontWeight: 700,
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 500,
    color: '#0c3c54',
    borderBottom: '1px solid #e6eef4',
    borderRight: '1px solid #e6eef4',
    verticalAlign: 'middle',
  },
  tdMuted: {
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 500,
    color: '#6a7d8c',
    borderBottom: '1px solid #e6eef4',
    borderRight: '1px solid #e6eef4',
    verticalAlign: 'middle',
    textAlign: 'center',
  },
  tdCenter: {
    padding: '12px 14px',
    fontSize: 13,
    color: '#0c3c54',
    borderBottom: '1px solid #e6eef4',
    borderRight: '1px solid #e6eef4',
    textAlign: 'center',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  },
  tdCenterLast: {
    padding: '12px 14px',
    fontSize: 13,
    color: '#0c3c54',
    borderBottom: '1px solid #e6eef4',
    textAlign: 'center',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  },
  rowActive: {
    background: '#f8fbfd',
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #021a30 0%, #032B4E 100%)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 600,
    flexShrink: 0,
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  nameText: {
    fontSize: 13,
    fontWeight: 600,
    color: '#0c3c54',
    fontFamily: 'Poppins, sans-serif',
  },
  expandCell: {
    padding: 0,
    background: '#f8fbfd',
    borderBottom: '1px solid #e6eef4',
  },
  foldPanel: {
    padding: '10px 14px 12px',
  },
  foldTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: '#0c3c54',
    margin: '0 0 8px 0',
    textAlign: 'left',
    fontFamily: 'Poppins, sans-serif',
  },
  assignedWrap: {
    display: 'grid',
    gap: 10,
  },
  assignedGroup: {
    background: '#fff',
    border: '1px solid #e6eef4',
    borderRadius: 8,
    padding: '10px 12px',
  },
  assignedHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
    font: '600 12px Poppins, sans-serif',
    color: '#032B4E',
  },
  assignedStats: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    font: '500 12px Poppins, sans-serif',
    color: '#6a7d8c',
    marginBottom: 2,
  },
  assignedCount: {
    font: '600 11px Poppins, sans-serif',
    color: '#6a7d8c',
  },
  chipWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: '#eff7fc',
    color: '#032B4E',
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    border: '1px solid #c4e0f0',
  },
  tagDepth: {
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: 10,
    color: '#6a7d8c',
  },
  emptyText: {
    textAlign: 'center',
    padding: '32px 16px',
    fontSize: 13,
    color: '#6a7d8c',
    fontFamily: 'Poppins, sans-serif',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(2, 26, 48, 0.55)',
    zIndex: 10000,
  },
  modal: {
    width: '92%',
    maxWidth: 460,
    background: '#fff',
    borderRadius: 12,
    overflow: 'hidden',
    border: '1px solid #e6eef4',
    boxShadow: '0 16px 40px rgba(12, 60, 84, 0.2)',
  },
  modalHead: {
    background: '#032B4E',
    color: '#fff',
    padding: '14px 18px',
    font: '600 16px Poppins, sans-serif',
  },
  modalBody: {
    padding: 20,
  },
  modalText: {
    margin: '0 0 8px',
    font: '500 14px Poppins, sans-serif',
    color: '#0c3c54',
  },
  modalHint: {
    margin: '0 0 20px',
    font: '400 13px Poppins, sans-serif',
    color: '#6a7d8c',
  },
};

const skuUi = {
  wrap: {
    width: '100%',
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 8,
    marginBottom: 10,
  },
  statChip: {
    background: '#f8fbfd',
    border: '1px solid #e6eef4',
    borderRadius: 8,
    padding: '8px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  statChipActive: {
    background: '#eff7fc',
    border: '1px solid #c4e0f0',
  },
  statValue: {
    font: '700 16px Poppins, sans-serif',
    color: '#032B4E',
    lineHeight: 1.2,
  },
  statLabel: {
    font: '500 11px Poppins, sans-serif',
    color: '#6a7d8c',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  trigger: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    minHeight: 42,
    padding: '8px 12px',
    background: '#fff',
    border: '1px solid #e6eef4',
    borderRadius: 8,
    cursor: 'pointer',
    fontFamily: 'Poppins, sans-serif',
    textAlign: 'left',
  },
  triggerText: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  panel: {
    background: '#fff',
    border: '1px solid #e6eef4',
    borderTop: 'none',
    borderRadius: '0 0 8px 8px',
    overflow: 'hidden',
  },
  searchWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: 10,
    padding: '8px 10px',
    border: '1px solid #e6eef4',
    borderRadius: 8,
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    font: '500 13px Poppins, sans-serif',
    color: '#0c3c54',
    background: 'transparent',
    minWidth: 0,
  },
  iconBtn: {
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: '#6a7d8c',
    display: 'inline-flex',
    padding: 0,
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    padding: '0 12px 8px',
    font: '500 11px Poppins, sans-serif',
    color: '#6a7d8c',
  },
  tree: {
    maxHeight: 280,
    overflowY: 'auto',
    padding: '0 8px 10px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px',
    borderRadius: 6,
    cursor: 'pointer',
  },
  chevron: {
    width: 16,
    height: 16,
    color: '#6a7d8c',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  chevronSpacer: { width: 16, flexShrink: 0 },
  checkbox: {
    width: 14,
    height: 14,
    accentColor: '#032B4E',
    cursor: 'pointer',
    flexShrink: 0,
  },
  levelBadge: {
    flexShrink: 0,
    font: '700 9px Poppins, sans-serif',
    letterSpacing: '0.04em',
    borderRadius: 4,
    padding: '2px 5px',
    lineHeight: 1.2,
  },
  rowLabel: {
    flex: 1,
    minWidth: 0,
    font: '13px Poppins, sans-serif',
    color: '#0c3c54',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  branchMeta: {
    flexShrink: 0,
    font: '600 11px Poppins, sans-serif',
    color: '#6a7d8c',
  },
  selectedBar: {
    display: 'grid',
    gap: 10,
    padding: '8px 12px 10px',
    borderTop: '1px solid #e6eef4',
    background: '#f8fbfd',
    maxHeight: 160,
    overflowY: 'auto',
  },
  selectedGroup: {
    minWidth: 0,
  },
  selectedGroupHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
    font: '600 12px Poppins, sans-serif',
    color: '#032B4E',
  },
  chipWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    background: '#eff7fc',
    color: '#032B4E',
    border: '1px solid #c4e0f0',
    borderRadius: 999,
    padding: '3px 8px',
    font: '600 11px Poppins, sans-serif',
  },
  chipRemove: {
    border: 'none',
    background: 'transparent',
    color: '#6a7d8c',
    cursor: 'pointer',
    display: 'inline-flex',
    padding: 0,
  },
  empty: {
    margin: 0,
    padding: '20px 8px',
    textAlign: 'center',
    font: '400 12px Poppins, sans-serif',
    color: '#6a7d8c',
  },
};

const pageCss = `
  .vendor-config-page label {
    font-family: Poppins, sans-serif !important;
    font-size: 13px !important;
    font-weight: 600 !important;
    color: #0c3c54 !important;
    margin-bottom: 8px !important;
    text-transform: none !important;
  }

  .vendor-config-page input {
    font-family: Poppins, sans-serif !important;
    font-size: 13px !important;
    font-weight: 500 !important;
    color: #0c3c54 !important;
  }

  .vendor-config-page input::placeholder {
    font-family: Poppins, sans-serif !important;
    font-size: 13px !important;
    font-weight: 400 !important;
    color: #6a7d8c !important;
  }

  @media (max-width: 720px) {
    .vendor-config-page [data-sku-stats] {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
  }
`;
